/*
PURPOSE: Fetches and displays user alignments (who a user is aligned with)

USE CASES:
- Display alignments on user profile pages
- Show who a contributor is following/aligned with
- Provide social discovery through alignment networks

DOMAIN BOUNDARIES:
- Responsible for fetching alignment data and orchestrating display
- Uses PersonGrid for visual presentation
- Does NOT handle tab navigation or URL state

ASSUMPTIONS:
- User ID is provided as prop
- API returns user alignments with basic user details
- Empty state encourages discovering new contributors
*/

import React, { useState, useEffect } from 'react';
import { getUserAlignmentsByUserId } from '../../api/api';
import PersonGrid from '../PersonGrid/PersonGrid';

interface AlignmentsGridProps {
  userId: string;
}

const AlignmentsGrid: React.FC<AlignmentsGridProps> = ({ userId }) => {
  const [alignments, setAlignments] = useState<Array<{
    userId: string;
    displayName?: string;
    firstName?: string;
    createdAt: string;
  }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAlignments = async () => {
      if (!userId) return;

      try {
        setIsLoading(true);
        setError(null);
        const response = await getUserAlignmentsByUserId(userId);

        // Backend already returns enriched data with userId field
        setAlignments(response.alignments);
      } catch (err) {
        console.error('Error fetching alignments:', err);
        setError('Failed to load alignments');
        setAlignments([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAlignments();
  }, [userId]);

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <PersonGrid
      people={alignments}
      isLoading={isLoading}
      emptyMessage="No alignments yet. Discover contributors to connect with."
    />
  );
};

export default AlignmentsGrid;
